package com.smashvoter.screens

import androidx.compose.runtime.*
import com.smashvoter.SmashColors
import com.smashvoter.api.ApiClient
import com.smashvoter.smashInput
import com.smashvoter.smashNavButton
import com.smashvoter.smashPrimaryButton
import com.smashvoter.state.AppState
import com.smashvoter.state.Screen
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLStyleElement
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.MouseEvent
import kotlin.math.roundToInt

// ---------------------------------------------------------------------------
// CSS injected once into <head>
// ---------------------------------------------------------------------------

private const val ONBOARDING_STYLE_ID = "onboarding-styles"

private val onboardingCss = """

/* ── Spinner ──────────────────────────────────────────────────────── */
@keyframes onb-spin {
    to { transform: rotate(360deg); }
}

/* ── Label pill animation ─────────────────────────────────────────── */
@keyframes pill-snap {
    0%   { opacity: 0.5; transform: scale(0.82) translateY(4px); }
    55%  { transform: scale(1.12) translateY(-2px); }
    100% { opacity: 1;  transform: scale(1)    translateY(0);    }
}
.onb-pill {
    animation: pill-snap 0.36s cubic-bezier(0.34, 1.56, 0.64, 1) both;
    display: inline-block;
}

/* ── Slider wrapper ───────────────────────────────────────────────── */
.onb-slider-wrapper {
    position: relative;
    height: 52px;
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
    -webkit-user-select: none;
    outline: none;
}

/* Track background */
.onb-slider-track {
    position: absolute;
    left: 11px;   /* inset by half-thumb (22px / 2) so thumb at 0 % / 100 % stays inside wrapper */
    right: 11px;
    top: 50%;
    transform: translateY(-50%);
    height: 8px;
    border-radius: 4px;
    background: #2C2C2E;
    box-shadow: inset 0 1px 4px rgba(0,0,0,0.5);
    overflow: visible;
}

/* Filled red portion */
.onb-slider-fill {
    position: absolute;
    left: 0; top: 0;
    height: 100%;
    border-radius: 4px;
    background: linear-gradient(90deg, #E10D05 0%, #B91C1C 100%);
    transition: width 0.14s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    pointer-events: none;
}

/* Tick dots */
.onb-tick {
    position: absolute;
    top: 50%;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #555;
    transform: translate(-50%, -50%);
    transition: background 0.2s ease, transform 0.2s ease;
    pointer-events: none;
    z-index: 2;
}
.onb-tick-done {
    background: rgba(255,255,255,0.55);
    transform: translate(-50%, -50%) scale(0.75);
}

/* Visual thumb */
.onb-slider-thumb {
    position: absolute;
    top: 50%;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #E10D05;
    border: 2.5px solid #FFFFFF;
    box-shadow: 0 2px 8px rgba(0,0,0,0.40);
    transform: translate(-50%, -50%);
    transition:
        left      0.18s cubic-bezier(0.34, 1.56, 0.64, 1),
        transform 0.14s cubic-bezier(0.34, 1.56, 0.64, 1),
        box-shadow 0.14s ease;
    pointer-events: none;
    z-index: 4;
}

/* While actively dragging: grow slightly */
.onb-slider-wrapper.onb-dragging {
    cursor: ew-resize;
}
.onb-slider-wrapper.onb-dragging .onb-slider-thumb {
    transform: translate(-50%, -50%) scale(1.18);
    box-shadow: 0 3px 14px rgba(0,0,0,0.45);
}

/* Keyboard focus ring */
.onb-slider-wrapper:focus-visible .onb-slider-thumb {
    box-shadow:
        0 0 0 3px rgba(225, 13, 5, 0.40),
        0 2px 8px rgba(0,0,0,0.40);
}

/* Tick labels */
.onb-tick-label {
    flex: 1;
    font-size: 10px;
    transition: color 0.18s ease;
    user-select: none;
    -webkit-user-select: none;
    line-height: 1.3;
}

""".trimIndent()

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

private val gamingLevelLabels = listOf(
    "I never play games",
    "I know how a controller feels",
    "I regularly play games",
    "I'm a gaming pro"
)

private val ssbExperienceLabels = listOf(
    "This is my first time",
    "I played it once or twice",
    "I play it sometimes",
    "I play it regularly"
)

private val skillLevelLabels = listOf(
    "Beginner",
    "Casual",
    "Intermediate",
    "Competitive"
)

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

@Composable
fun OnboardingScreen(apiClient: ApiClient) {
    DisposableEffect(Unit) {
        if (document.getElementById(ONBOARDING_STYLE_ID) == null) {
            val style = document.createElement("style") as HTMLStyleElement
            style.id = ONBOARDING_STYLE_ID
            style.textContent = onboardingCss
            document.head?.appendChild(style)
        }
        onDispose {}
    }

    var nickname by remember { mutableStateOf("") }
    var gamingLevel by remember { mutableStateOf(0) }
    var ssbExperience by remember { mutableStateOf(0) }
    var skillLevel by remember { mutableStateOf(0) }
    var isSubmitting by remember { mutableStateOf(false) }
    var submitError by remember { mutableStateOf<String?>(null) }
    var successRating by remember { mutableStateOf<Double?>(null) }
    // null = still checking, true = done checking
    var statusChecked by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()

    // ── On first load: check if the user already submitted onboarding ─
    // If so, jump straight to the success state without showing the form.
    LaunchedEffect(Unit) {
        val sid = AppState.sessionId
        if (sid != null) {
            try {
                val status = apiClient.getOnboardingStatus(sid)
                if (status.completed) {
                    successRating = status.rating ?: 0.0
                }
            } catch (_: Exception) {
                // If the check fails just show the form — safe fallback.
            }
        }
        statusChecked = true
    }

    // ── Page shell: shared nav bar + content area ────────────────────
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            minHeight(100.vh)
            backgroundColor(Color(SmashColors.background))
        }
    }) {
        // ── Nav bar ───────────────────────────────────────────────────
        Div(attrs = {
            style {
                backgroundColor(Color(SmashColors.surface))
                property("border-bottom", "1px solid ${SmashColors.border}")
                padding(14.px, 24.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(10.px)
                }
            }) {
                Img(src = "smash-logo.svg", attrs = {
                    style { width(36.px); height(36.px); property("flex-shrink", "0") }
                })
                Span(attrs = {
                    style {
                        color(Color(SmashColors.textPrimary))
                        fontSize(20.px)
                        fontWeight("900")
                        letterSpacing((-0.3).px)
                    }
                }) { Text("SmashVoter") }
            }
            Button(attrs = {
                onClick {
                    scope.launch {
                        val sid = AppState.sessionId
                        if (sid != null) try { apiClient.logout(sid) } catch (_: Exception) {}
                        AppState.clearSession()
                    }
                }
                style { smashNavButton() }
            }) { Text("Logout") }
        }

        // ── Content area ──────────────────────────────────────────────
        Div(attrs = {
            style {
                flex(1)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                padding(24.px)
                property("box-sizing", "border-box")
            }
        }) {
            when {
                // Loading state
                !statusChecked -> Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        gap(12.px)
                    }
                }) {
                    Div(attrs = {
                        style {
                            width(32.px)
                            height(32.px)
                            borderRadius(50.percent)
                            border(3.px, LineStyle.Solid, Color(SmashColors.border))
                            property("border-top-color", SmashColors.accent)
                            property("animation", "onb-spin 0.75s linear infinite")
                        }
                    }) {}
                    Span(attrs = {
                        style {
                            color(Color(SmashColors.textSecondary))
                            fontSize(14.px)
                        }
                    }) { Text("Loading…") }
                }

                // Already completed — show success card
                successRating != null -> OnboardingSuccessContent(rating = successRating!!)

                // Form
                else -> Div(attrs = {
                    style {
                        backgroundColor(Color(SmashColors.surface))
                        padding(40.px)
                        borderRadius(16.px)
                        property("box-shadow", "0 8px 32px rgba(0,0,0,0.6)")
                        border(1.px, LineStyle.Solid, Color(SmashColors.border))
                        maxWidth(480.px)
                        width(100.percent)
                        property("box-sizing", "border-box")
                    }
                }) {
                    H1(attrs = {
                        style {
                            textAlign("center")
                            marginTop(0.px)
                            marginBottom(4.px)
                            color(Color(SmashColors.textPrimary))
                            fontSize(26.px)
                            fontWeight("900")
                            letterSpacing((-0.5).px)
                        }
                    }) { Text("Tell us about yourself") }

                    P(attrs = {
                        style {
                            textAlign("center")
                            color(Color(SmashColors.textSecondary))
                            marginTop(0.px)
                            marginBottom(32.px)
                            fontSize(14.px)
                        }
                    }) { Text("This helps us to balance the start positions of the tournament") }

                    // ── Nickname ──────────────────────────────────────
                    FieldLabel("Your nickname")
                    Input(type = InputType.Text, attrs = {
                        value(nickname)
                        placeholder("e.g. SmashKing67")
                        onInput { nickname = it.value }
                        style { smashInput() }
                    })

                    // ── Sliders ───────────────────────────────────────
                    SliderSection(
                        label = "How often do you play games?",
                        value = gamingLevel,
                        labels = gamingLevelLabels,
                        onValueChange = { gamingLevel = it }
                    )
                    SliderSection(
                        label = "How often do you play Super Smash Bros?",
                        value = ssbExperience,
                        labels = ssbExperienceLabels,
                        onValueChange = { ssbExperience = it }
                    )
                    SliderSection(
                        label = "What's your Super Smash Bros skill level?",
                        value = skillLevel,
                        labels = skillLevelLabels,
                        onValueChange = { skillLevel = it }
                    )

                    // ── Error message ─────────────────────────────────
                    if (submitError != null) {
                        P(attrs = {
                            style {
                                color(Color("#F87171"))
                                fontSize(13.px)
                                textAlign("center")
                                marginTop(8.px)
                                marginBottom(0.px)
                            }
                        }) { Text(submitError!!) }
                    }

                    // ── Submit ────────────────────────────────────────
                    Div(attrs = { style { marginTop(16.px) } }) {
                        Button(attrs = {
                            if (isSubmitting) disabled()
                            onClick {
                                if (nickname.isBlank()) {
                                    submitError = "Please enter a nickname"
                                    return@onClick
                                }
                                submitError = null
                                isSubmitting = true
                                val sid = AppState.sessionId ?: return@onClick
                                scope.launch {
                                    try {
                                        val result = apiClient.submitOnboarding(
                                            nickname      = nickname.trim(),
                                            gamingLevel   = gamingLevel,
                                            ssbExperience = ssbExperience,
                                            skillLevel    = skillLevel,
                                            sessionId     = sid
                                        )
                                        successRating = result.rating
                                    } catch (e: Exception) {
                                        submitError = "Something went wrong, please try again"
                                    } finally {
                                        isSubmitting = false
                                    }
                                }
                            }
                            style {
                                smashPrimaryButton()
                                if (isSubmitting) property("opacity", "0.6")
                            }
                        }) { Text(if (isSubmitting) "Saving…" else "Let's go!") }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

@Composable
private fun FieldLabel(text: String) {
    Div(attrs = {
        style {
            color(Color(SmashColors.textSecondary))
            fontSize(12.px)
            fontWeight("600")
            letterSpacing(0.6.px)
            property("text-transform", "uppercase")
            marginBottom(6.px)
        }
    }) { Text(text) }
}

@Composable
private fun SliderSection(
    label: String,
    value: Int,
    labels: List<String>,
    onValueChange: (Int) -> Unit
) {
    val max = labels.size - 1
    // Fraction 0..1 used for thumb calc() and tick-label positions
    val thumbFraction = if (max > 0) value.toDouble() / max else 0.0

    Div(attrs = { style { marginBottom(32.px) } }) {
        // ── Question label + selected answer stacked vertically ──────────
        // key(value) causes a full DOM remount each change → CSS animation re-fires
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.FlexStart)
                marginBottom(14.px)
                gap(8.px)
            }
        }) {
            Span(attrs = {
                style {
                    color(Color(SmashColors.textSecondary))
                    fontSize(12.px)
                    fontWeight("600")
                    letterSpacing(0.6.px)
                    property("text-transform", "uppercase")
                }
            }) { Text(label) }
            key(value) {
                Span(attrs = {
                    classes("onb-pill")
                    style {
                        backgroundColor(Color(SmashColors.accentButton))
                        color(Color(SmashColors.textPrimary))
                        padding(4.px, 12.px)
                        borderRadius(20.px)
                        fontSize(12.px)
                        fontWeight("700")
                        property("white-space", "nowrap")
                    }
                }) { Text(labels[value]) }
            }
        }

        // ── Slider ────────────────────────────────────────────────────
        Div(attrs = {
            classes("onb-slider-wrapper")
            tabIndex(0)

            // Keyboard: Arrow keys change value; fresh `value` is captured each recomposition
            onKeyDown { e ->
                val newValue = when (e.key) {
                    "ArrowRight", "ArrowUp"   -> (value + 1).coerceAtMost(max)
                    "ArrowLeft",  "ArrowDown" -> (value - 1).coerceAtLeast(0)
                    "Home"                    -> 0
                    "End"                     -> max
                    else                      -> return@onKeyDown
                }
                e.preventDefault()
                onValueChange(newValue)
            }

            // Mouse / touch: document-level listeners so the drag never "escapes"
            ref { wrapper ->
                var isDragging = false

                // Converts an absolute clientX pixel into a slider value 0..max.
                // The track is inset 14 px on each side inside the wrapper, so we
                // subtract that inset before computing the fraction — this keeps
                // snap boundaries exactly at the visual midpoints between tick dots.
                fun calcValue(clientX: Int): Int {
                    val rect = wrapper.getBoundingClientRect()
                    val inset = 11.0  // half of thumb width (22px)
                    val fraction = ((clientX.toDouble() - rect.left - inset) / (rect.width - 2.0 * inset))
                        .coerceIn(0.0, 1.0)
                    return (fraction * max).roundToInt()
                }

                // ── Mouse ─────────────────────────────────────────────
                val onMouseMove = EventListener { e ->
                    if (isDragging) onValueChange(calcValue(e.unsafeCast<MouseEvent>().clientX))
                }

                val onMouseUp = EventListener { _ ->
                    if (isDragging) {
                        isDragging = false
                        wrapper.classList.remove("onb-dragging")
                    }
                }

                val onMouseDown = EventListener { e ->
                    val me = e.unsafeCast<MouseEvent>()
                    me.preventDefault()
                    isDragging = true
                    wrapper.classList.add("onb-dragging")
                    onValueChange(calcValue(me.clientX))
                }

                // ── Touch ─────────────────────────────────────────────
                val onTouchMove = EventListener { e ->
                    if (isDragging) {
                        e.preventDefault()
                        val clientX = e.asDynamic().touches[0].clientX as Int
                        onValueChange(calcValue(clientX))
                    }
                }

                val onTouchEnd = EventListener { _ ->
                    isDragging = false
                    wrapper.classList.remove("onb-dragging")
                }

                val onTouchStart = EventListener { e ->
                    e.preventDefault()
                    isDragging = true
                    wrapper.classList.add("onb-dragging")
                    val clientX = e.asDynamic().touches[0].clientX as Int
                    onValueChange(calcValue(clientX))
                }

                // Register: mousedown/touch on the wrapper; move+up on the document
                // so dragging outside the element boundary still works.
                wrapper.addEventListener("mousedown",  onMouseDown)
                wrapper.addEventListener("touchstart", onTouchStart)
                wrapper.addEventListener("touchmove",  onTouchMove)
                wrapper.addEventListener("touchend",   onTouchEnd)
                document.addEventListener("mousemove", onMouseMove)
                document.addEventListener("mouseup",   onMouseUp)

                onDispose {
                    wrapper.removeEventListener("mousedown",  onMouseDown)
                    wrapper.removeEventListener("touchstart", onTouchStart)
                    wrapper.removeEventListener("touchmove",  onTouchMove)
                    wrapper.removeEventListener("touchend",   onTouchEnd)
                    document.removeEventListener("mousemove", onMouseMove)
                    document.removeEventListener("mouseup",   onMouseUp)
                }
            }
        }) {
            // Track + fill + tick marks
            Div(attrs = { classes("onb-slider-track") }) {
                Div(attrs = {
                    classes("onb-slider-fill")
                    style { width((thumbFraction * 100.0).percent) }
                }) {}

                for (i in 0..max) {
                    val tickPct = if (max > 0) i.toDouble() / max * 100.0 else 0.0
                    Div(attrs = {
                        classes("onb-tick")
                        if (i <= value) classes("onb-tick-done")
                        style { left(tickPct.percent) }
                    }) {}
                }
            }

            // Visual thumb — positioned via calc() to follow the inset track.
                            // calc(11px + f * (100% - 22px)) maps f=0 → 11px, f=1 → 100%-11px.
            Div(attrs = {
                classes("onb-slider-thumb")
                style { property("left", "calc(11px + $thumbFraction * (100% - 22px))") }
            }) {}
        }

        // ── Tick labels centered below each dot ───────────────────────
        Div(attrs = {
            style {
                position(Position.Relative)
                marginTop(8.px)
                height(40.px)
            }
        }) {
            labels.forEachIndexed { index, tickLabel ->
                val active = index == value
                val tickFraction = if (max > 0) index.toDouble() / max else 0.0
                Span(attrs = {
                    classes("onb-tick-label")
                    style {
                        position(Position.Absolute)
                        // Mirror the thumb calc so labels sit exactly under their tick dot
                        property("left", "calc(11px + $tickFraction * (100% - 22px))")
                        top(0.px)
                        property("transform", "translateX(-50%)")
                        textAlign("center")
                        property("max-width", "90px")
                        color(Color(if (active) SmashColors.accent else SmashColors.textSecondary))
                        fontWeight(if (active) "700" else "400")
                    }
                }) { Text(tickLabel) }
            }
        }

    }
}

// ---------------------------------------------------------------------------
// Success screen
// ---------------------------------------------------------------------------

/** Card-only success content — rendered inside the shared page shell. */
@Composable
private fun OnboardingSuccessContent(rating: Double) {
    Div(attrs = {
        style {
            backgroundColor(Color(SmashColors.surface))
            padding(48.px, 40.px)
            borderRadius(16.px)
            property("box-shadow", "0 8px 32px rgba(0,0,0,0.6)")
            border(1.px, LineStyle.Solid, Color(SmashColors.border))
            maxWidth(440.px)
            width(100.percent)
            property("box-sizing", "border-box")
            textAlign("center")
        }
    }) {
        Div(attrs = { style { fontSize(64.px); marginBottom(16.px) } }) {
            Text("🏆")
        }

        H1(attrs = {
            style {
                color(Color(SmashColors.textPrimary))
                fontSize(26.px)
                fontWeight("900")
                marginTop(0.px)
                marginBottom(8.px)
                letterSpacing((-0.5).px)
            }
        }) { Text("You're all set!") }

        P(attrs = {
            style {
                color(Color(SmashColors.textSecondary))
                fontSize(15.px)
                lineHeight("1.6")
                marginTop(0.px)
                marginBottom(24.px)
            }
        }) {
            Text("Your profile is saved. You're ready to compete in the tournament. May the best Smasher win!")
        }

        // Rating badge
        Div(attrs = {
            style {
                property("display", "inline-flex")
                alignItems(AlignItems.Center)
                gap(8.px)
                backgroundColor(Color(SmashColors.accentButton))
                padding(10.px, 20.px)
                borderRadius(24.px)
                marginBottom(32.px)
            }
        }) {
            Span(attrs = {
                style {
                    color(Color(SmashColors.textPrimary))
                    fontSize(12.px)
                    fontWeight("600")
                    property("text-transform", "uppercase")
                    letterSpacing(0.6.px)
                }
            }) { Text("Starting rating") }
            Span(attrs = {
                style {
                    color(Color(SmashColors.textPrimary))
                    fontSize(20.px)
                    fontWeight("900")
                }
            }) { Text(rating.toInt().toString()) }
        }
    }
}
